<script lang="ts" setup>
import './scss/Home.scss';
import './scss/ContratacionTerrestre.scss';
import { ref, computed, onMounted, ComponentPublicInstance, onBeforeUnmount } from 'vue';
import Footer from './components/Footer.vue';
import {
  findCompaniesGPS,
  createOrUpdateClient,
  createPolicyForTrygerLatam,
  findClientByTributaryCode,
} from '../../../repositories/landing.repository';
import { wrongDepartureDateTime, today } from '../../../helpers';
import storePolicy from '@store/policy';
import { useRouter } from 'vue-router';
import { BModal } from 'bootstrap-vue-next';
import WhatsappButton from './components/WhatsappButton.vue';
import { validationNIF } from '../../../helpers/validationNIF';
import GeoRefChileRepository from '../../../repositories/geoRefChile.repository';
import { IRegion } from '../../../repositories/geoRefChile.repository';
import {
  finalCalculationCH,
  IFinalCalculation,
} from '../../../helpers/finalCalculationCH';
import GeoRefSpainRepository, { IMunicipio } from '@repositories/geoRefSpain.repository';
import moment from 'moment';
import { useI18n } from 'vue-i18n'
import CaptchaCloudflare from './components/CaptchaCloudflare.vue';

type CaptchaExpose = {
  reset: () => void
}

const { t } = useI18n();
const store = storePolicy();
const router = useRouter();
const refForm2 = ref<any>(null);
const refForm3 = ref<any>(null);
const inputsHidden = ref<boolean>(true);
const isOpenModal = ref<boolean>(false);
const checkedAuth = ref<boolean>(false);
const loading = ref<boolean>(false);
const policyId = ref<number | null>(1);
const error = ref<string>('');
const municipiosES = ref<IMunicipio[]>([]);
const companySelectedByParams = ref<{ nombre?: string; id: number } | null>(
  null,
);
const finalCalculation = ref<IFinalCalculation | null>(null);
const tokenCaptcha = ref<string | null>(null);
const captchaRef = ref<ComponentPublicInstance<CaptchaExpose> | null>(null);
const isSmallScreen = ref<boolean>(false);
const MOBILE_BREAKPOINT = 768;
const MOBILE_HALF_WIDTH_KEYS = new Set([
  'startDate',
  'startTime',
  'endDate',
  'endTime',
  'endForm',
  'finalCalculation'
]);
const MOBILE_STOPS_KEYS = new Set(['stops', 'stop1', 'stop2', 'stop3']);

const updateScreenSize = () => {
  if (typeof window === 'undefined') return;
  isSmallScreen.value = window.innerWidth < MOBILE_BREAKPOINT;
};

onMounted(() => {
  updateScreenSize();
  window.addEventListener('resize', updateScreenSize);
  const { subAsset, product, ...rest } = store.data;

  // Validamos si hay alguna propiedad (excepto subAsset y product) que sea null
  const thereAreNullProps = Object.values(rest).some((valor) => valor === null);

  // Validamos la relación entre subAsset y product
  const isProductRequiredButMissing = subAsset !== null && product === null;

  if (thereAreNullProps || isProductRequiredButMissing) {
    router.push('/');
  }
});

onMounted(async () => {
  const regiones = await GeoRefSpainRepository.getMunicipios();

  municipiosES.value = regiones;
  const comapaniesGPS = await findCompaniesGPS();
  fieldsForm2.value = fieldsForm2.value.map((field: any) => {
    if (field.key === 'companyGPSId') {
      field.options = comapaniesGPS;
      const gpsCompany = store.data.queryParams.gpsCompany;
      if (gpsCompany) {
        const company = comapaniesGPS.find((company) => {
          return company.slug === gpsCompany;
        });
        if (company) {
          companySelectedByParams.value = company;
          field.value = company;
          field.disabled = true;
          field.customClass =
            'multiselect--disabled .multiselect__select inputLanding';
        }
      }
    }
    if (field.key === 'region') {
      field.options = regiones;
    }

    return field;
  });
});

onBeforeUnmount(() => {
  if (typeof window === 'undefined') return;
  window.removeEventListener('resize', updateScreenSize);
});

computed(() => fieldsForm3.value);

const fieldsForm1: any = ref([
  {
    key: 'nif',
    title: t('landing.terrestre.contratacion_es.nif_del_cliente'),
    type: 'text',
    validations: {
      rules: {
        required: true,
      },
    },
    placeholder: t('landing.terrestre.contratacion_es.nif_del_cliente'),
    value: null,
    cols: 12,
    customClass: 'inputLanding text-uppercase',
    hidden: false,
    errors: [],
  },
]);

const fieldsForm2: any = ref([
  {
    key: 'tradeName',
    title: t('landing.terrestre.contratacion_es.nombre_del_cliente'),
    type: 'text',
    validations: {
      rules: {
        required: true,
      },
    },
    placeholder: t('landing.terrestre.contratacion_es.nombre_del_cliente'),
    value: null,
    cols: 12,
    customClass: 'inputLanding',
    formatValue: (e: string) => e?.toUpperCase() ?? '',
  },
  {
    key: 'email',
    title: t('landing.terrestre.contratacion_es.email_del_cliente'),
    type: 'email',
    validations: {
      rules: {
        required: true,
        email: true,
      },
    },
    placeholder: t('landing.terrestre.contratacion_es.email_del_cliente'),
    value: store.data.email ?? null,
    cols: 12,
    customClass: 'inputLanding',
  },
  {
    key: 'phone1',
    title: t('landing.terrestre.contratacion_es.telefono_del_cliente'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_es.telefono_del_cliente'),
    value: null,
    cols: 12,
    customClass: 'inputLanding',
  },
  {
    key: 'companyGPSId',
    title: t('landing.terrestre.contratacion_es.empresa_gps'),
    selectLabel: null,
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
    },
    disabled: false,
    placeholder: t('landing.terrestre.contratacion_es.empresa_gps'),
    value: null,
    cols: 12,
    customClass: 'inputSelect',
    options: [],
  },
  {
    key: 'address',
    title: t('landing.terrestre.contratacion_es.direccion_del_cliente'),
    type: 'text',
    validations: {
      rules: {
        required: true,
      },
    },
    placeholder: t('landing.terrestre.contratacion_es.direccion_del_cliente'),
    value: null,
    cols: 12,
    customClass: 'inputLanding',
  },
  {
    key: 'postCode',
    title: t('landing.terrestre.contratacion_es.codigo_postal_placeholder'),
    type: 'text',
    validations: {
      rules: {
        required: true,
      },
    },
    placeholder: t('landing.terrestre.contratacion_es.codigo_postal_placeholder'),
    value: null,
    cols: 12,
    customClass: 'inputLanding',
  },
  {
    key: 'region',
    title: t('landing.terrestre.contratacion_es.provincia'),
    selectLabel: null,
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
    },
    placeholder: t('landing.terrestre.contratacion_es.provincia'),
    value: null,
    cols: 12,
    customClass: 'inputSelect',
    options: [],
    listenSelect: true,
  },
  {
    key: 'comuna',
    title: t('landing.terrestre.contratacion_es.municipio'),
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
    },
    placeholder: t('landing.terrestre.contratacion_es.municipio'),
    value: null,
    options: municipiosES,
    cols: 12,
    customClass: 'inputSelect',
  },
]);

const fieldsForm3: any = ref([
  { slot: true, key: 'title' },
  {
    key: 'typeOfMerchandise',
    title: t('landing.terrestre.contratacion_es.tipo_de_mercancia'),
    type: 'text',
    validations: {
      rules: {
        required: true,
      },
    },
    placeholder: t('landing.terrestre.contratacion_es.tipo_de_mercancia'),
    value: store.data?.asset?.nombre,
    cols: 12,
    customClass: 'inputLanding',
    disabled: true,
  },
  {
    key: 'indemnificationLimit',
    title: t('landing.terrestre.contratacion_es.limite_de_indemnizacion'),
    type: 'number',
    validations: {
      rules: {
        required: true,
      },
    },
    placeholder: t('landing.terrestre.contratacion_es.limite_de_indemnizacion'),
    value: store.data.indemnificationLimit,
    cols: 6,
    customClass: 'inputLanding',
    disabled: true,
  },
  {
    key: 'indemnificationLimit-calc',
    title: t('landing.terrestre.contratacion_es.precio'),
    type: 'text',
    value: `€ ${store.data.endPrice} + Impuestos`,
    validations: {
      rules: {
        required: true,
      },
    },
    placeholder: t('landing.terrestre.contratacion_es.precio_mas_impuestos'),
    disabled: true,
    cols: 6,
    customClass: 'inputLanding',
  },
  {
    key: 'originRegion',
    title: t('landing.terrestre.contratacion_es.provincia_de_origen'),
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
    },
    options: municipiosES,
    placeholder: t('landing.terrestre.contratacion_es.provincia_de_origen'),
    value: null,
    cols: 6,
    customClass: 'inputSelect',
    listenSelect: true,
  },
  {
    key: 'originComuna',
    title: t('landing.terrestre.contratacion_es.municipio_de_origen'),
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
    },
    placeholder: t('landing.terrestre.contratacion_es.municipio_de_origen'),
    value: null,
    cols: 6,
    options: municipiosES,
    customClass: 'inputSelect',
    disabled: true,
  },
  {
    key: 'destinationRegion',
    title: t('landing.terrestre.contratacion_es.provincia_de_destino_final'),
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
    },
    placeholder: t('landing.terrestre.contratacion_es.provincia_de_destino_final'),
    value: null,
    options: municipiosES,
    cols: 6,
    customClass: 'inputSelect',
    listenSelect: true,
  },
  {
    key: 'destinationComuna',
    title: t('landing.terrestre.contratacion_es.municipio_destino_final'),
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
    },
    placeholder: 'Elije la municipio de Destino',
    value: null,
    cols: 6,
    options: municipiosES,
    customClass: 'inputSelect',
    disabled: true,
  },
  {
    key: 'stops',
    title: t('landing.terrestre.contratacion_es.paradas_intermedias'),
    selectLabel: null,
    type: 'select',
    placeholder: t('landing.terrestre.contratacion_es.paradas_intermedias_placeholder'),
    value: null,
    cols: 3,
    customClass: 'inputSelect',
    validations: {
      rules: {
        required: true,
      },
    },
    options: [
      { nombre: 'Si', id: 1 },
      { nombre: 'No', id: 0 },
    ],
    listenSelect: true,
  },
  {
    key: 'stop1',
    title: t('landing.terrestre.contratacion_es.parada_1'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_es.parada_1'),
    value: null,
    cols: 3,
    disabled: true,
    validations: {
      rules: {
        required: false,
      },
    },
    customClass: 'inputLanding inputLanding--mt',
  },
  {
    key: 'stop2',
    title: t('landing.terrestre.contratacion_es.parada_2'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_es.parada_2'),
    value: null,
    cols: 3,
    disabled: true,
    customClass: 'inputLanding inputLanding--mt',
  },
  {
    key: 'stop3',
    title: t('landing.terrestre.contratacion_es.parada_3'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_es.parada_3'),
    value: null,
    cols: 3,
    disabled: true,
    customClass: 'inputLanding inputLanding--mt',
  },
  {
    key: 'startDate',
    title: t('landing.terrestre.contratacion_es.fecha_inicio_viaje'),
    type: 'date',
    placeholder: t('landing.terrestre.contratacion_es.fecha_inicio_viaje'),
    value: null,
    cols: 6,
    min: today(),
    validations: {
      rules: {
        required: true,
      },
    },
    customClass: 'inputLanding',
  },
  {
    key: 'startTime',
    title: t('landing.terrestre.contratacion_es.hora_inicio_viaje'),
    type: 'time',
    placeholder: t('landing.terrestre.contratacion_es.hora_inicio_viaje'),
    value: null,
    cols: 6,
    validations: {
      rules: {
        required: true,
      },
    },
    customClass: 'inputLanding',
    listenFocus: true,
  },
  {
    key: 'endDate',
    title: t('landing.terrestre.contratacion_es.fecha_fin_viaje'),
    type: 'date',
    placeholder: t('landing.terrestre.contratacion_es.fecha_fin_viaje'),
    value: null,
    cols: 6,
    min: today(),
    validations: {
      rules: {
        required: true,
      },
    },
    customClass: 'inputLanding',
  },
  {
    key: 'endTime',
    title: t('landing.terrestre.contratacion_es.hora_fin_viaje'),
    type: 'time',
    placeholder: t('landing.terrestre.contratacion_es.hora_fin_viaje'),
    value: null,
    cols: 6,
    validations: {
      rules: {
        required: true,
      },
    },
    customClass: 'inputLanding',
  },
  {
    key: 'documentDescription',
    title: t('landing.terrestre.contratacion_es.documentos_del_viaje'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_es.documentos_del_viaje'),
    value: null,
    cols: 6,
    validations: {
      rules: {
        required: true,
      },
    },
    customClass: 'inputLanding',
  },
  {
    key: 'document',
    title: t('landing.terrestre.contratacion_es.adjuntar_documentos'),
    type: 'file',
    placeholder: t('landing.terrestre.contratacion_es.adjuntar_documentos'),
    value: null,
    cols: 6,
    hidden: false,
    customClass: 'inputFile',
  },
  {
    key: 'truckPlate',
    title: t('landing.terrestre.contratacion_es.placa_tractor'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_es.placa_tractor'),
    value: null,
    cols: 4,
    validations: {
      rules: {
        required: true,
      },
    },
    customClass: 'inputLanding inputLanding--fs',
  },
  {
    key: 'box-trailer',
    title:  t('landing.terrestre.contratacion_es.caja_de_carga'),
    selectLabel: null,
    type: 'select',
    placeholder: t('landing.terrestre.contratacion_es.caja_de_carga'),
    value: null,
    cols: 4,
    options: [
      { nombre: 'Si', id: 1 },
      { nombre: 'No', id: 0 },
    ],
    validations: {
      rules: {
        required: true,
      },
    },
    hidden: false,
    customClass: 'inputSelect inputLanding--fs',
    listenSelect: true,
  },
  {
    key: 'trailerPlate',
    title: t('landing.terrestre.contratacion_es.placa_caja'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_es.placa_caja'),
    value: null,
    cols: 4,
    customClass: 'inputLanding inputLanding--fs',
    validations: {
      rules: {
        required: false,
      },
    },
    disabled: true,
  },
  {
    key: 'driverName',
    title: t('landing.terrestre.contratacion_es.nombre_del_conductor'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_es.nombre_del_conductor'),
    value: null,
    cols: 12,
    validations: {
      rules: {
        required: true,
      },
    },
    customClass: 'inputLanding',
  },
  {
    key: 'driverCellphone',
    title: t('landing.terrestre.contratacion_es.nombre_del_conductor'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_es.nombre_del_conductor'),
    value: null,
    cols: 12,
    validations: {
      rules: {
        required: true,
        max: 12,
        min: 8,
      },
    },
    customClass: 'inputLanding',
  },
  {
    key: 'driverEmail',
    title: t('landing.terrestre.contratacion_es.email_del_conductor'),
    type: 'email',
    placeholder: t('landing.terrestre.contratacion_es.email_del_conductor'),
    value: null,
    cols: 12,
    validations: {
      rules: {
        email: true,
      },
    },
    hidden: false,
    customClass: 'inputLanding',
  },
  { slot: true, key: 'finalCalculation', cols: 12 },
  { slot: true, key: 'endForm', cols: 12 },
]);

const responsiveFieldsForm1 = computed(() =>
  fieldsForm1.value.map((field: any) => ({
    ...field,
    cols:
      isSmallScreen.value && MOBILE_STOPS_KEYS.has(field.key)
        ? 6
        : isSmallScreen.value && !MOBILE_HALF_WIDTH_KEYS.has(field.key)
        ? 12
        : field.cols,
  })),
);

const responsiveFieldsForm2 = computed(() =>
  fieldsForm2.value.map((field: any) => ({
    ...field,
    cols:
      isSmallScreen.value && MOBILE_STOPS_KEYS.has(field.key)
        ? 6
        : isSmallScreen.value && !MOBILE_HALF_WIDTH_KEYS.has(field.key)
        ? 12
        : field.cols,
  })),
);

const responsiveFieldsForm3 = computed(() =>
  fieldsForm3.value.map((field: any) => ({
    ...field,
    cols:
      isSmallScreen.value && MOBILE_STOPS_KEYS.has(field.key)
        ? 6
        : isSmallScreen.value && !MOBILE_HALF_WIDTH_KEYS.has(field.key)
        ? 12
        : field.cols,
  })),
);

const handleOnSubmitNIF = async (e: any) => {
  // "12345678Z"
  // "X1234567L"

  // const calculos = new CalculosDNICIF();
  // const isValidNIF = calculos.ValidarNIF(e.items.nif);

  const isValidNIF = validationNIF(e.items.nif);

  if (!e.isFormValid || !isValidNIF) {
    return (fieldsForm1.value[0].errors = ['NIF es invalido']);
  }
  const nifWithoutSpecialChars = e.items.nif.replace(/[-.]/g, '');
  const { client } = await findClientByTributaryCode(nifWithoutSpecialChars);

  if (client) {
    store.setProp({ key: 'client', value: client });
  }

  if (client) {
    const updatesClientInputs = [
      { key: 'tradeName', value: client.tradeName },
      { key: 'phone1', value: client.phone1 },
      {
        key: 'companyGPSId',
        value: {
          id: companySelectedByParams.value?.id ?? client.companyGPS.id,
          nombre:
            companySelectedByParams.value?.nombre ??
            client.companyGPS.description,
        },
      },
      { key: 'address', value: client.address },
      { key: 'region', value: { nombre: client.regionProvince } },
      { key: 'comuna', value: { nombre: client.location } },
      { key: 'postCode', value: client.postCode },
    ];

    updateFieldsInArray(fieldsForm2.value, updatesClientInputs, null);
  }
  fieldsForm1.value.find((field: any) => field.key === 'nif').disabled = true;

  inputsHidden.value = false;

  if (!client) {
    store.setProp({ key: 'client', value: { nif: e.items.nif } });
  }
};
// Handles form changes for refForm3
const handleSelectChange = async (e: any) => {
  const updateFields: {
    key: string;
    disabled?: boolean;
    'validations.rules.required'?: boolean;
    options?: any[];
  }[] = [];

  const updateCommonFields = (key: string, isEnabled: boolean) => {
    updateFields.push(
      { key, disabled: !isEnabled },
      { key, 'validations.rules.required': isEnabled },
    );
  };

  switch (e.input.key) {
    case 'box-trailer':
      updateCommonFields('trailerPlate', !!e.value.id);
      break;

    case 'originRegion':
    case 'destinationRegion':
    //   const cities = await GeoRefChileRepository.getComunas(e.value.id);
      const cityKey =
        e.input.key === 'originRegion' ? 'originComuna' : 'destinationComuna';
      await refForm3.value.setValue({ key: cityKey, value: null });
      updateFieldsInArray(
        fieldsForm3.value,
        [{ key: cityKey, options: municipiosES.value, disabled: false }],
        null,
      );
      return;
    default:
      ['stop1', 'stop2', 'stop3'].forEach((stop) =>
        updateCommonFields(stop, !!e.value.id),
      );
      break;
  }

  updateFieldsInArray(fieldsForm3.value, updateFields, null);
};

// La funcion busca las localidades al elejir la provincia
// Al obtener de que provincia es el cliente ya podemos generar el calculo final de la poliza
const handleGetCities = async () => {
  const { items } = await refForm2.value.getValues();
//   const comunas = await GeoRefChileRepository.getComunas(items.region.id);
  await refForm2.value.setValue({ key: 'comuna', value: null });

  const preservedValues = {
    tradeName: items.tradeName,
    email: items.email,
    phone1: items.phone1,
    companyGPSId: items.companyGPSId,
    address: items.address,
    region: items.region,
  };

  updateFieldsInArray(
    fieldsForm2.value,
    [{ key: 'comuna', options: municipiosES.value, value: null }],
    preservedValues,
  );

  finalCalculation.value = finalCalculationCH(
    store.data.endPrice,
    store.pricing.uf,
  );
};

const updateFieldsInArray = (
  array: any,
  updates: any,
  preservedValues: any,
) => {
  for (const update of updates) {
    const field = array.find((field: any) => field.key === update.key);
    if (field) {
      if ('type' in update) {
        field.type = update.type;
      }
      if ('options' in update) {
        field.options = update.options;
      }
      if ('value' in update) {
        field.value = update.value;
      }
      if ('disabled' in update) {
        field.disabled = update.disabled;
      }

      if ('customClass' in update) {
        field.customClass = update.customClass;
      }
    }
  }

  if (preservedValues) {
    for (const key in preservedValues) {
      const field = array.find((field: any) => field.key === key);
      if (field) {
        field.value = preservedValues[key];
      }
    }
  }
};

// Submit form with all values
const handleOnSubmitPolicies = async ({ items, isFormValid }: any) => {
  const { items: itemsForm2, isFormValid: isFormValidForm2 } =
    await refForm2.value.getValues();

  if (true) {
    return (error.value = 'Api de españa aun sin funcionar');
  }

  if (!tokenCaptcha.value) {
    return (error.value = 'Por favor, verifica el reCAPTCHA primero');
  }

  if (!checkedAuth.value) {
    return (error.value = 'Términos y Condiciones no aceptados');
  }

  if (!isFormValidForm2) {
    return (error.value = 'El formulario debe estar completo');
  }
  if (!isFormValid) {
    return (error.value = 'El formulario debe estar completo');
  }

  const {
    address,
    companyGPSId,
    email,
    phone1,
    tradeName,
    comuna,
    region,
    postCode,
  } = itemsForm2;

  const {
    originComuna,
    typeOfMerchandise,
    destinationComuna,
    indemnificationLimit,
    truckPlate,
    trailerPlate,
    driverName,
    documentDescription,
    driverCellphone,
    driverEmail,
    startDate,
    endDate,
    startTime,
    endTime,
    stops,
    stop1,
    stop2,
    stop3,
    document,
    originRegion,
    destinationRegion,
  } = items;

  const isOkDepartureDateTime = wrongDepartureDateTime(startDate, startTime, 'cl');

  if (isOkDepartureDateTime) {
    return (error.value =
      'La hora de salida no puede ser menor a dentro de 90 minutos.');
  }
  if (tradeName.split(' ').length < 2) {
    return (error.value = 'Debe proporcionar al menos un nombre y un apellido');
  }

  try {
    loading.value = true;
    const nifWithoutSpecialChars = store.client.nif.replace(/[-.]/g, '');

    const createOrUpdateClientData = {
      tradeName,
      address,
      email,
      phone1,
      location: comuna.nombre,
      postCode,
      nif: nifWithoutSpecialChars,
      regionProvince: region.nombre,
      companyGPSId: store.client?.companyGPS?.id ?? companyGPSId.id,
      urlDomain: window.location.origin,
    };

    const client = await createOrUpdateClient(
      store.client.id,
      createOrUpdateClientData,
      'CL',
    );
    store.setProp({
      key: 'client',
      value: { ...store.client, id: client.data.client.id },
    });

    const travelStartDate = new Date(`${startDate}T${startTime}`);
	const travelEndDate = new Date(`${endDate}T${endTime}`);
	const fechaISOstart = moment(travelStartDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
	const fechaISOend = moment(travelEndDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    const assetId = store.data.asset.id;
	const subAssetId = store.data.subAsset?.id ?? null;
    const productId = store.data.product?.id ?? null;

    const createPolicyData: any = {
      tradeName,
      assetId,
	  subAssetId,
	  productId,
      phone1,
      typeOfMerchandise,
      indemnificationLimit,
      originRegionProvince: originRegion.nombre,
      destinationRegionProvince: destinationRegion.nombre,
      originLocation: originComuna.nombre ?? originComuna,
      destinationLocation: destinationComuna.nombre ?? destinationComuna,
      stop1,
      stop2,
      stop3,
      trailerPlate,
      truckPlate,
      driverName,
      documentDescription,
      driverCellphone,
      driverEmail,
      travelStartDate: fechaISOstart,
      travelEndDate: fechaISOend,
      hasStops: stops.nombre,
      companyGPSId: companyGPSId.id,
      document: document?.length ? document[0].image : null,
      clientName: tradeName,
      tributaryCode: store.client.nif,
      address,
      postalCode: postCode,
      location: comuna.nombre,
      regionProvince: region,
      email,
      endPrice: finalCalculation?.value?.primaTotal,
      token: tokenCaptcha.value,
    };

    const urlForPayment = await createPolicyForTrygerLatam(
      client.data.client.id,
      client.data.manager.id,
      createPolicyData,
    );

    window.location.replace(urlForPayment);
    loading.value = false;
  } catch (e) {
    error.value = 'Ocurrió un Error.';
    loading.value = false;
  } finally{
	 captchaRef.value?.reset()
  }
};

const hideModal = () => {
  isOpenModal.value = false;
};
const handleTokenCaptcha = (token: string) => {
  tokenCaptcha.value = token;
};

const handleOnExpired = () => {
  tokenCaptcha.value = null;
};
</script>

<template>
   <h1 class="landing__title">{{ t('landing.terrestre.contratacion_es.seguro_de_mercancias_por_trayecto') }}</h1>
  <h2 class="landing__subtitle">{{ t('landing.terrestre.contratacion_es.ya_falta_menos') }}</h2>
  <h2 class="landing__subtitle">{{ t('landing.terrestre.contratacion_es.ayudanos_completando') }}</h2>
  <div class="contratacion">
    <div class="contratacion__container">
      <h2 class="text-center text-white">{{ t('landing.terrestre.contratacion_es.datos_del_cliente') }}</h2>
      <Form
        ref="refTableForm"
        :inputs="responsiveFieldsForm1"
        @onSubmit="handleOnSubmitNIF"
      >
        <template #buttons>
          <div class="d-flex" v-if="inputsHidden">
            <CButton
              class="p-4 w-100 my-2 px-2"
              background="background-green"
              style="font-size: 1rem"
            >
              <template #content>{{ t('landing.terrestre.contratacion_es.comprobar_nif') }}</template>
            </CButton>
          </div>
        </template>
      </Form>
      <template v-if="!inputsHidden">
        <Form
          ref="refForm2"
          :inputs="responsiveFieldsForm2"
          @select="handleGetCities"
        ></Form>
        <Form
          ref="refForm3"
          :inputs="responsiveFieldsForm3"
          @onSubmit="handleOnSubmitPolicies"
          @select="handleSelectChange"
        >
          <template #title>
            <h2 class="text-center text-white mt-5">{{ t('landing.terrestre.contratacion_es.datos_del_viaje') }}</h2>
          </template>
          <template #endForm>
            <h2 class="text-white fs-5 text-decoration-underline">
				{{ t('landing.terrestre.contratacion_es.terminos_y_condiciones') }}
            </h2>
            <b-form-checkbox v-model="checkedAuth" name="check-button" switch>
              <p class="text-white">
				{{ t('landing.terrestre.contratacion_es.declaro_haber_leido') }}
              </p>
            </b-form-checkbox>
          </template>
          <template #finalCalculation>
            <div v-if="finalCalculation" class="container p-0 mt-2">
              <b-card class="rounded-4">
                <h2 class="text-center text-primary">
				  {{ t('landing.terrestre.contratacion_es.calculo_de_prima_fina_y_pago') }}
                </h2>
                <b-row class="mt-3">
                  <b-col>
                    <b-list-group>
                      <b-list-group-item
                        class="d-flex justify-content-between align-items-center"
                      >
                        <span>{{ t('landing.terrestre.contratacion_es.prima_neta') }}</span>
                        <span class="font-weight-bold"
                          >€ {{ finalCalculation.primaNeta }}</span
                        >
                      </b-list-group-item>
                      <b-list-group-item
                        class="d-flex justify-content-between align-items-center"
                      >
                        <span>{{ t('landing.terrestre.contratacion_es.iva') }}</span>
                        <span class="font-weight-bold"
                          >€ {{ finalCalculation.IVA }}</span
                        >
                      </b-list-group-item>
                      <b-list-group-item
                        class="d-flex justify-content-between align-items-center bg-primary text-white"
                      >
                        <span><strong>{{ t('landing.terrestre.contratacion_es.prima_total') }}</strong></span>
                        <span class="font-weight-bold"
                          >€ {{ finalCalculation.primaTotal }}</span
                        >
                      </b-list-group-item>
                    </b-list-group>
                  </b-col>
                </b-row>
              </b-card>
            </div>
          </template>

          <template #buttons>
            <p v-if="error" class="text-danger-emphasis mt-3">{{ error }}</p>
            <CaptchaCloudflare
				ref="captchaRef"
				@verified="handleTokenCaptcha"
				@expired="handleOnExpired"
			/>
            <div class="d-flex">
              <CButton
                class="p-4 w-100 my-2 px-2"
                background="background-green"
                style="font-size: 1.5rem"
              >
                <template #content>
                  <div>
                    <BSpinner v-if="loading" />
                    <span v-else>{{ t('landing.terrestre.contratacion_es.pagar_el_seguro') }}</span>
                  </div>
                </template>
              </CButton>
            </div>
          </template>
        </Form>
      </template>
    </div>
  </div>
  <Footer />
  <WhatsappButton />
  <BModal
    :modelValue="isOpenModal"
    hide-footer
    static
    @hide="hideModal"
    size="lg"
    v-if="policyId"
  >
    <h4 class="text-center">Términos y Condiciones</h4>
    <ul>
      <li class="mb-4">
		{{ t('landing.terrestre.contratacion_es.punto_1') }}
      </li>
      <li class="mb-4">
		{{ t('landing.terrestre.contratacion_es.punto_2') }}
      </li>
      <li class="mb-4">
		{{ t('landing.terrestre.contratacion_es.punto_3') }}
      </li>
      <li class="mb-4">
		{{ t('landing.terrestre.contratacion_es.punto_4') }}
      </li>
    </ul>
  </BModal>
</template>
